import React, { useEffect, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { GeneralSelect } from 'v2/form/CustomSelectors/GeneralSelect';
import FormLabel from 'v2/form/FormLabel/FormLabel';
import { FormControl } from 'react-bootstrap';
import { useFilesDropzone } from 'v2/common/FilesDropzone/hooks';
import {
  BulkFilesFormFields,
  FileTypeDisplayString,
} from 'v2/utils/drmFileUtils';
import { FileType } from 'v2/models';
import FilesDropzone from 'v2/common/FilesDropzone/FilesDropzone';
import { DropzoneFileItem } from 'v2/common/DropzoneFileItem';

const options = Object.values(FileType).map((key) => ({
  label: FileTypeDisplayString[key],
  value: key,
}));

const selectStyles = {
  control: {
    width: '100%',
    borderColor: 'var(--light-gray)',
  },
  dropdownIndicator: {
    right: '10px',
    justifyContent: 'flex-end',
  },
};

export const BulkFilesForm = () => {
  const { control, setError, clearErrors, register, setValue } =
    useFormContext<BulkFilesFormFields>();

  const onFilesSuccess = (data: string) => {
    clearErrors('files');
    setValue('files', data, { shouldValidate: true });
  };

  const onFilesErrors = () => {
    setError('files', {
      type: 'custom',
      message: 'There are files with errors',
    });
  };

  const dropzoneRef = useRef<HTMLDivElement>(null);

  const { maxAllowedBulkUploadedFiles, files, dropzone } = useFilesDropzone({
    dropzoneRef,
    onSuccess: onFilesSuccess,
    onError: onFilesErrors,
  });

  useEffect(() => {
    const fileUploadIDs = files
      .map((file) => file.fileId)
      .filter((fileUploadID): fileUploadID is string => Boolean(fileUploadID));
    setValue('files', JSON.stringify(fileUploadIDs));
  }, [files]);

  return (
    <>
      <div>
        <FormLabel label={`File Type`} required={false} />
        <Controller
          name="type"
          control={control}
          rules={{
            required: true,
          }}
          render={({ field: { onChange } }) => {
            return (
              <GeneralSelect
                options={options}
                handleChange={(option) => onChange(option?.value)}
                customStyles={selectStyles}
              />
            );
          }}
        />
      </div>
      <FormControl
        type="hidden"
        aria-label="files-hidden-input"
        data-testid="filesInfoInput"
        data-cy="filesInput"
        {...register?.('files', {
          required: true,
          validate: (value) => {
            const fileUploadIDs: string[] = JSON.parse(value);
            return fileUploadIDs.length > 0;
          },
        })}
      />
      <FilesDropzone
        ref={dropzoneRef}
        maxAllowedBulkUploadedFiles={maxAllowedBulkUploadedFiles}
      />

      {files.map((file, index) => (
        <DropzoneFileItem
          key={index}
          file={file}
          onDelete={() => dropzone?.removeFile(file)}
        />
      ))}
    </>
  );
};
